import * as React from 'react';
import '../../style/patients.css';
import '../../style/utils.css';
import { useState } from 'react';
import ConfirmModal from '../ui/confirmModal';
import Address from './address';
import { validateName, validateEmail, validatePhone } from '../../Utils/validation';
import { Patient } from '../../../Models/Patient';
import { useNavigate } from 'react-router-dom';
import AuthContext from '../../store/authContext';
import ApiClient from '../../Services/ApiClient';

interface PatientsProps {
  patients: Patient[];
  onAddPatient?: (patient: Omit<Patient, 'id' | 'creationDate'>) => Promise<Patient | null>;
  onUpdatePatient?: (patient: Patient) => Promise<void>;
  onRemovePatient?: (id: number) => Promise<void>;
  onFetchPatients?: () => void;
}

const initialFormState = {
  firstName: '',
  lastName: '',
  phoneNumber: '',
  email: '',
};

const Patients: React.FC<PatientsProps> = ({
  patients,
  onAddPatient,
  onUpdatePatient,
  onRemovePatient,
  onFetchPatients,
}) => {
  const navigate = useNavigate();
  const auth = React.useContext(AuthContext);
  const [showModal, setShowModal] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);
  const [form, setForm] = useState(initialFormState);
  const [errors, setErrors] = useState<{ firstName?: string; lastName?: string; phoneNumber?: string; email?: string }>({});
  const [editId, setEditId] = useState<number | null>(null);

  const [showAddressModal, setShowAddressModal] = useState(false);
  const [addressForm, setAddressForm] = useState<{ id?: number; patientId?: number; city: string; street: string; buildingNumber: string }>({ city: '', street: '', buildingNumber: '' });
  const [newPatientId, setNewPatientId] = useState<number | null>(null);

  const url = import.meta.env.VITE_API_URL;

  const [printHtml, setPrintHtml] = useState<string | null>(null);
  const [printTitle, setPrintTitle] = useState<string>('');
  const [confirmPatientDelete, setConfirmPatientDelete] = useState<number | null>(null);

  // Redirect to home if token missing/invalid
  React.useEffect(() => {
    const token = auth?.token ?? localStorage.getItem('token');
    try {
      const expired = auth && typeof auth.isTokenExpired === 'function'
        ? auth.isTokenExpired(token ?? '')
        : true;
      if (!token || expired) {
        navigate('/');
      }
    } catch {
      navigate('/');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth?.token]);

  const openAddModal = () => {
    setForm(initialFormState);
    setEditId(null);
    setShowModal(true);
  };

  const openEditModal = (patient: Patient) => {
    setForm({
      firstName: patient.firstName,
      lastName: patient.lastName,
      phoneNumber: patient.phoneNumber,
      email: patient.email,
    });
    setEditId(patient.id);
    setShowModal(true);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setErrors(prev => ({ ...prev, [e.target.name]: undefined }));
  };

  async function handleAddressSubmit(e: React.FormEvent) {
    e.preventDefault();
    // If addressForm.id exists -> update, else create
    try {
      if (addressForm.id) {
        await ApiClient.put(`/address/${addressForm.id}`, {
          id: addressForm.id,
          patientId: addressForm.patientId,
          city: addressForm.city,
          street: addressForm.street,
          buildingNumber: addressForm.buildingNumber,
          creationDate: new Date().toISOString()
        });
      } else {
        await ApiClient.post('/address', {
          patientId: addressForm.patientId,
          city: addressForm.city,
          street: addressForm.street,
          buildingNumber: addressForm.buildingNumber,
          creationDate: new Date().toISOString()
        });
      }
      setShowAddressModal(false);
      setNewPatientId(null);
      setAddressForm({ city: '', street: '', buildingNumber: '' });
    } catch (err) {
      // show simple alert (you can replace with showAlert)
      alert('שגיאה בשמירת הכתובת');
    }
  }

  async function openEditAddressModal(patientId: number) {
    try {
      // Try to fetch address for patient (controller supports GET /address/patient/{patientId})
      const addr: any = await ApiClient.get(`/address/patient/${patientId}`);
      if (addr) {
        setAddressForm({
          id: addr.id,
          patientId: addr.patientId,
          city: addr.city ?? '',
          street: addr.street ?? '',
          buildingNumber: addr.buildingNumber ?? '',
        });
      } else {
        // no address yet
        setAddressForm({ patientId, city: '', street: '', buildingNumber: '' });
      }
    } catch {
      // fallback: no address found or error
      setAddressForm({ patientId, city: '', street: '', buildingNumber: '' });
    }
    setShowAddressModal(true);
  }

  function handlePatientAdded(patientId: number) {
    setNewPatientId(patientId);
    // prefill patientId on address form
    setAddressForm({ patientId, city: '', street: '', buildingNumber: '' });
    setShowAddressModal(true);
  }

  const handleAddClick = () => {
    openAddModal();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Validate fields with consistent messages for localization
    const firstNameErr = validateName(form.firstName) || 'שם פרטי לא תקין';
    const lastNameErr = validateName(form.lastName) || 'שם משפחה לא תקין';
    const phoneErr = validatePhone(form.phoneNumber) || 'טלפון לא תקין';
    const emailErr = validateEmail(form.email) || 'אימייל לא תקין';
    const newErrors = { firstName: firstNameErr ?? undefined, lastName: lastNameErr ?? undefined, phoneNumber: phoneErr ?? undefined, email: emailErr ?? undefined };
    setErrors(newErrors);
    if (firstNameErr || lastNameErr || phoneErr || emailErr) return;
    if (editId !== null && onUpdatePatient) {
      await onUpdatePatient({
        id: editId,
        firstName: form.firstName,
        lastName: form.lastName,
        phoneNumber: form.phoneNumber,
        email: form.email,
        creationDate: patients.find(p => p.id === editId)?.creationDate || new Date().toISOString(),
      } as Patient);
    } else if (onAddPatient) {
      // await created patient from wrapper
      const created = await onAddPatient({
        firstName: form.firstName,
        lastName: form.lastName,
        phoneNumber: form.phoneNumber,
        email: form.email,
      } as Omit<Patient, 'id' | 'creationDate'>);
      if (created && created.id) {
        handlePatientAdded(created.id);
      }
    }
    setShowModal(false);
  };

  return (
    <div className="patients-list page-padding">
      <h2>רשימת מטופלים</h2>
      <div className="mb-16">
        <button className="btn-add" onClick={handleAddClick}>הוסף מטופל</button>
        <button className="btn-add" onClick={async () => {
          try {
            const res: any = await ApiClient.get('/patients/print/all');
            setPrintHtml(res);
            setPrintTitle('כל המטופלים');
          } catch (err) { alert('שגיאה ביצירת הדפסה'); }
        }}>הדפס את כל המטופלים</button>
      </div>
      <div className="patients-header">
        <div>שם פרטי</div>
        <div>שם משפחה</div>
        <div>טלפון</div>
        <div>אימייל</div>
        <div>נוצר בתאריך</div>
        <div>פעולות</div>
      </div>
      {patients ? patients.map((patient) => (
        <div className="patient-row" key={patient.id}>
          <div className="patient-firstname" style={{ fontWeight: 600 }}>{patient.firstName}</div>
          <div className="patient-lastname">{patient.lastName}</div>
          <div className="patient-phone">{patient.phoneNumber}</div>
          <div className="patient-email">{patient.email}</div>
          <div className="patient-created">{new Date(patient.creationDate).toLocaleDateString()}</div>
          <div className="patient-actions">
            <button className="btn" onClick={() => { setSelectedPatient(patient); }}>{'פרטים'}</button>
            <button className="btn" onClick={() => openEditModal(patient)}>עדכן</button>
            <button className="btn btn-danger" onClick={() => { setConfirmPatientDelete(patient.id); }}>{'מחק'}</button>
            <button className="btn btn-on-dark" onClick={() => openEditAddressModal(patient.id)}>ערוך כתובת</button>
            <button className="btn" onClick={async () => {
              try {
                const res = await ApiClient.get(`/patients/print/${patient.id}`);
                setPrintHtml(res);
                setPrintTitle(`${patient.firstName} ${patient.lastName}`);
              } catch { alert('שגיאה ביצירת הדפסה'); }
            }}>הדפס</button>
            <button className="btn ml-8" onClick={async () => {
              try {
                const enabled: any = await ApiClient.get('/whatsapp/enabled');
                if (!enabled?.enabled) { alert('WhatsApp disabled for this site'); return; }
                const custom = prompt('הקלד הודעה מותאמת אישית (השאר ריק להודעת ברירת המחדל)');
                await ApiClient.post(`/whatsapp/send/patient/${patient.id}`, { Message: custom ?? undefined });
                alert('WhatsApp נשלח בהצלחה');
              } catch (err) { alert('שליחת WhatsApp נכשלה'); }
            }}>שלח WhatsApp</button>
          </div>
        </div>
      )) : <></>}

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowModal(false)}>×</button>
            <form onSubmit={handleSubmit}>
              <div>
                <label>שם פרטי:</label>
                <input name="firstName" value={form.firstName} onChange={handleChange} required />
                {errors.firstName ? <div className="error-message">{errors.firstName}</div> : null}
              </div>
              <div>
                <label>שם משפחה:</label>
                <input name="lastName" value={form.lastName} onChange={handleChange} required />
                {errors.lastName ? <div className="error-message">{errors.lastName}</div> : null}
              </div>
              <div>
                <label>טלפון:</label>
                <input name="phoneNumber" value={form.phoneNumber} onChange={handleChange} required />
                {errors.phoneNumber ? <div className="error-message">{errors.phoneNumber}</div> : null}
              </div>
              <div>
                <label>אימייל:</label>
                <input name="email" value={form.email} onChange={handleChange} required type="email" />
                {errors.email ? <div className="error-message">{errors.email}</div> : null}
              </div>
              <div className="mt-8">
                <button className="btn btn-primary" type="submit">{editId !== null ? 'עדכן' : 'הוסף'} מטופל</button>
              </div>
            </form>
          </div>
        </div>
      )}
      <ConfirmModal
        open={confirmPatientDelete != null}
        title={'Confirm delete'}
        message={<span>האם למחוק מטופל עם מזהה <strong>{confirmPatientDelete}</strong>?</span>}
        onConfirm={async () => { if (confirmPatientDelete) { await onRemovePatient?.(confirmPatientDelete); setConfirmPatientDelete(null); } }}
        onCancel={() => setConfirmPatientDelete(null)}
        loading={false}
      />

      {/* Detail modal for selected patient (read-only view with actions) */}
      {selectedPatient && (
        <div className="modal-overlay" onClick={() => setSelectedPatient(null)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setSelectedPatient(null)}>×</button>
            <h3>{selectedPatient.firstName} {selectedPatient.lastName}</h3>
            <div style={{marginTop:8}}>
              <div><strong>טלפון:</strong> {selectedPatient.phoneNumber}</div>
              <div><strong>אימייל:</strong> {selectedPatient.email}</div>
              <div><strong>נוצר בתאריך:</strong> {new Date(selectedPatient.creationDate).toLocaleString()}</div>
            </div>
            <div style={{display:'flex', gap:8, marginTop:12}}>
              <button className="btn" onClick={() => { openEditModal(selectedPatient); setSelectedPatient(null); }}>{'ערוך'}</button>
              <button className="btn btn-danger" onClick={async () => { setConfirmPatientDelete(selectedPatient.id); }}>{'מחק'}</button>
              <button className="btn" onClick={() => { openEditAddressModal(selectedPatient.id); }}>{'כתובת'}</button>
              <button className="btn" onClick={async () => { try { const res = await ApiClient.get(`/patients/print/${selectedPatient.id}`); setPrintHtml(res); setPrintTitle(`${selectedPatient.firstName} ${selectedPatient.lastName}`); } catch { alert('שגיאה ביצירת הדפסה'); } }}>{'הדפס'}</button>
              <button className="btn" onClick={async () => { try { const enabled: any = await ApiClient.get('/whatsapp/enabled'); if (!enabled?.enabled) { alert('WhatsApp disabled for this site'); return; } const custom = prompt('הקלד הודעה מותאמת אישית (השאר ריק להודעת ברירת המחדל)'); await ApiClient.post(`/whatsapp/send/patient/${selectedPatient.id}`, { Message: custom ?? undefined }); alert('WhatsApp נשלח בהצלחה'); } catch { alert('שליחת WhatsApp נכשלה'); } }}>{'שלח WhatsApp'}</button>
            </div>
          </div>
        </div>
      )}

      {showAddressModal && (
        <div className="modal-overlay" onClick={() => setShowAddressModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowAddressModal(false)}>×</button>
            <h3>{addressForm && addressForm.id ? 'עריכת כתובת' : 'הוסף כתובת'}</h3>
            <form onSubmit={handleAddressSubmit}>
              <div>
                <label>עיר:</label>
                <input name="city" value={addressForm.city} onChange={(e) => setAddressForm({ ...addressForm, city: e.target.value })} required />
              </div>
              <div>
                <label>רחוב:</label>
                <input name="street" value={addressForm.street} onChange={(e) => setAddressForm({ ...addressForm, street: e.target.value })} required />
              </div>
              <div>
                <label>מספר בניין:</label>
                <input name="buildingNumber" value={addressForm.buildingNumber} onChange={(e) => setAddressForm({ ...addressForm, buildingNumber: e.target.value })} required />
              </div>
              <div className="mt-12">
                <button className="btn btn-primary" type="submit">{addressForm && addressForm.id ? 'עדכן כתובת' : 'הוסף כתובת'}</button>
                <button className="btn ml-8" type="button" onClick={() => setShowAddressModal(false)}>ביטול</button>
              </div>
            </form>
            <div className="mt-12">
              <Address
                city={addressForm.city}
                street={addressForm.street}
                buildingNumber={addressForm.buildingNumber}
                creationDate={new Date().toISOString()}
              />
            </div>
          </div>
        </div>
      )}
      {/* Print preview modal */}
      {printHtml && (
        <div className="modal-overlay" onClick={() => { setPrintHtml(null); setPrintTitle(''); }}>
          <div className="modal-content print-modal-content" onClick={e => e.stopPropagation()}>
            <button className="modal-close" onClick={() => { setPrintHtml(null); setPrintTitle(''); }}>×</button>
            <h3>{printTitle || 'הדפסה'}</h3>
            <div className="print-preview-container">
              <iframe title="print-preview" srcDoc={printHtml} className="print-iframe" />
            </div>
            <div className="mt-8 text-right">
              <button onClick={() => {
                const iframe = document.querySelector('iframe[title="print-preview"]') as HTMLIFrameElement | null;
                try {
                  iframe?.contentWindow?.focus();
                  iframe?.contentWindow?.print();
                } catch { window.print(); }
              }}>הדפס</button>
              <button className="ml-8" onClick={() => { setPrintHtml(null); setPrintTitle(''); }}>סגור</button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default Patients;